import { createInterface } from 'node:readline';
import type { FutureXClient } from '@fim/futurex-protocol';
import { readCreds } from '../creds.js';
import { createPermissionGate, type AskFn, type Gate } from '../permissions.js';
import { findRepoRoot } from '../repo.js';
import { createDirectClient } from './direct.js';
import { createGatewayClient } from './gateway.js';

/** Which backend the CLI is talking to. */
export interface ResolvedClient {
  client: FutureXClient;
  mode: 'gateway' | 'local';
  email?: string;
}

export interface ClientOptions {
  /** Permission gate for mutating tools. Interactive callers (REPL) pass their own. */
  readonly gate?: Gate;
  /** Repo root tools are confined to (defaults to the detected root). */
  readonly repoRoot?: string;
  readonly useRepo?: boolean;
  readonly useMemory?: boolean;
}

/**
 * Default permission gate for one-shot / standalone commands: stored per-project
 * always-allow/deny decisions apply first; only an 'ask' state prompts (TTY), and
 * an always-* answer here persists like everywhere else. Denies when stdin isn't
 * interactive (never hang a pipe).
 */
function defaultGate(repoRoot: string): Gate {
  const ask: AskFn = async (req) => {
    if (process.stdin.isTTY !== true) return 'no';
    const rl = createInterface({ input: process.stdin, output: process.stderr });
    try {
      const answer = (
        await new Promise<string>((resolve) =>
          rl.question(`approve [${req.kind}] ${req.preview}? [y/N/a]lways/[d]eny-always `, resolve),
        )
      )
        .trim()
        .toLowerCase();
      if (answer === 'a' || answer === 'always') return 'always-allow';
      if (answer === 'd' || answer === 'deny' || answer === 'never') return 'always-deny';
      return answer === 'y' || answer === 'yes' ? 'yes' : 'no';
    } finally {
      rl.close();
    }
  };
  return createPermissionGate(repoRoot, ask);
}

/**
 * Choose the backend: the FIM gateway when logged in (`futurex login`), else the
 * local direct-to-DeepSeek fallback. The gateway client is a full agent client —
 * it executes the tools the gateway's model requests. Both implement the same
 * {@link FutureXClient}, so callers don't care which they get.
 */
export function createClient(options: ClientOptions = {}): ResolvedClient {
  const creds = readCreds();
  if (creds?.accessToken) {
    const repoRoot = options.repoRoot ?? findRepoRoot();
    return {
      client: createGatewayClient(creds, {
        gate: options.gate ?? defaultGate(repoRoot),
        repoRoot,
        ...(options.useRepo !== undefined ? { useRepo: options.useRepo } : {}),
        ...(options.useMemory !== undefined ? { useMemory: options.useMemory } : {}),
      }),
      mode: 'gateway',
      email: creds.email,
    };
  }
  return { client: createDirectClient(), mode: 'local' };
}
